import { useState, useMemo, useCallback, useEffect } from "react";
import { CalendarFeedSection } from "@/components/CalendarFeedSection";
import { useSubscription } from "@/hooks/useSubscription";
import { ManageSubscriptionDialog } from "@/components/ManageSubscriptionDialog";
import { EmbeddedCheckoutDialog } from "@/components/EmbeddedCheckoutDialog";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PLAN_CONFIG } from "@/lib/plans";
import { UpgradeHint } from "@/components/UpgradeHint";
import { useFamily } from "@/hooks/useFamily";
import { useAuth } from "@/hooks/useAuth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import { Users, Check, Mail, Clock, Shield, HeartHandshake, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChildProfileDrawer } from "@/components/ChildProfileDrawer";

import { toast } from "sonner";

import { signOut } from "@/lib/auth";
import { ChevronLeft, ChevronRight, ClipboardList, Loader2, AlertCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeatureFlags, SELF_SERVICE_FLAGS, ADMIN_ONLY_FLAGS } from "@/hooks/useFeatureFlags";
import type { FeatureFlag } from "@/hooks/useFeatureFlags";

const EMOJI_OPTIONS = ["🧒", "👧", "👦", "🧒🏽", "👧🏿", "👦🏼", "🐱", "🦊", "🐶", "🦄", "🐻", "🐰"];

// ── Types ──────────────────────────────────────────────
interface Child {
  id: string;
  name: string;
  avatar_emoji: string;
  family_id: string;
}

interface FamilyMember {
  id: string;
  user_id: string;
  display_name: string | null;
  child_facing_name: string | null;
  role: string;
}


export default function Settings() {
  const { user } = useAuth();
  const { familyId, family } = useFamily();
  const { hasTogether, hasPlus } = useEntitlements(familyId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);

  // ── Data queries ────────────────────────────────────
  const { data: children = [] } = useQuery({
    queryKey: ["children", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("*")
        .eq("family_id", familyId!)
        .order("created_at");
      if (error) throw error;
      return data as Child[];
    },
    enabled: !!familyId,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["family-members", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_members")
        .select("id, user_id, display_name, child_facing_name, role")
        .eq("family_id", familyId!);
      if (error) throw error;
      return data as FamilyMember[];
    },
    enabled: !!familyId,
  });




  const { data: childCoparents = [] } = useQuery({
    queryKey: ["child-coparents-all", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("child_coparents" as any)
        .select("child_id")
        .eq("family_id", familyId!);
      if (error) throw error;
      return data as unknown as { child_id: string }[];
    },
    enabled: !!familyId,
  });

  const hasSplitChildren = (family as any)?.home_mode === "split" || childCoparents.length > 0;
  const childrenWithCoparent = useMemo(
    () => new Set(childCoparents.map((c) => c.child_id)),
    [childCoparents]
  );

  // Loading handled by AppShell

  return (
    <div className="min-h-full bg-background px-4 pt-safe pb-6">
      <div className="mx-auto max-w-sm space-y-3">
        <h1 className="text-center font-heading text-lg font-semibold tracking-tight text-foreground pb-1">Innstillinger</h1>

        {/* 1. Child profiles */}
        <ChildProfilesSection
          children={children}
          familyId={familyId}
          queryClient={queryClient}
          onSelectChild={setSelectedChild}
        />

        <Separator />

        {/* 2. Co-parent */}
        <CoparentSection
          familyId={familyId}
          members={members}
          userId={user?.id}
          childCoparents={childCoparents}
          children={children}
          hasTogether={hasTogether}
          hasPlus={hasPlus}
        />

        {/* 3. Checklist templates — only for split/custody families */}
        {(family as any)?.home_mode === "split" && (
          <>
            <Separator />
            <ChecklistTemplatesSection familyId={familyId} children={children} />
          </>
        )}

        <Separator />

        {/* AI-hjelp section */}
        <AiCoachingSection familyId={familyId} hasPlus={hasPlus} />

        <Separator />

        {/* Calendar feed */}
        <CalendarFeedSection />

        <Separator />

        {/* 4. Subscription */}
        <SubscriptionSection />

        <Separator />

        {/* 3. Account */}
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-foreground pt-0.5">Konto</h2>

          <FeatureFlagsToggle familyId={familyId} children={children} />

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="w-full text-destructive">
                Logg ut
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Logg ut?</AlertDialogTitle>
                <AlertDialogDescription>
                  Er du sikker på at du vil logge ut?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction
                  onClick={async () => {
                    await signOut();
                    navigate("/login", { replace: true });
                  }}
                >
                  Logg ut
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Child profile drawer */}
      <ChildProfileDrawer
        child={selectedChild}
        onClose={() => setSelectedChild(null)}
        familyId={selectedChild?.family_id ?? familyId ?? ""}
        members={members}
        userId={user?.id}
        allFamilyChildren={children}
        childrenWithCoparent={childrenWithCoparent}
      />

    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Section: Child Profiles
// ═══════════════════════════════════════════════════════
function ChildProfilesSection({
  children,
  familyId,
  queryClient,
  onSelectChild,
}: {
  children: Child[];
  familyId: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
  onSelectChild: (child: Child) => void;
}) {
  const [newName, setNewName] = useState("");
  const [newEmoji, setNewEmoji] = useState("🧒");
  const [showAdd, setShowAdd] = useState(false);

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!familyId || !newName.trim()) return;
      const { error } = await supabase
        .from("children")
        .insert({ family_id: familyId, name: newName.trim(), avatar_emoji: newEmoji });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["children", familyId] });
      setNewName("");
      setNewEmoji("🧒");
      setShowAdd(false);
      toast.success("Barn lagt til");
    },
    onError: () => toast.error("Kunne ikke legge til barn"),
  });

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base font-semibold">Barn</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0">
        {children.map((child) => (
          <div
            key={child.id}
            className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => onSelectChild(child)}
          >
            <span className="text-2xl">{child.avatar_emoji}</span>
            <span className="font-medium flex-1">{child.name}</span>
            <span className="text-muted-foreground">›</span>
          </div>
        ))}

        {showAdd ? (
          <div className="space-y-2 rounded-lg border border-dashed p-3">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Navn på barnet"
            />
            <div className="flex flex-wrap gap-1.5">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => setNewEmoji(emoji)}
                  className={`rounded-md p-1.5 text-xl transition-all ${
                    newEmoji === emoji
                      ? "bg-primary/10 ring-2 ring-primary"
                      : "hover:bg-muted"
                  }`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => addMutation.mutate()}
                disabled={!newName.trim() || addMutation.isPending}
              >
                Legg til
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)}>
                Avbryt
              </Button>
            </div>
          </div>
        ) : (
          <AddChildButton childCount={children.length} onAdd={() => setShowAdd(true)} />
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════
// Helper: Add Child Button with plan gate
// ═══════════════════════════════════════════════════════
function AddChildButton({ childCount, onAdd }: { childCount: number; onAdd: () => void }) {
  return (
    <Button variant="outline" size="sm" className="w-full" onClick={onAdd}>
      + Legg til barn
    </Button>
  );
}
// ═══════════════════════════════════════════════════════
// Section: Subscription
// ═══════════════════════════════════════════════════════
function SubscriptionSection() {
  const { plan, isTrialing, trialEndsAt, isFree } = useSubscription();
  const { familyId } = useFamily();
  const { hasPlus, hasTogether } = useEntitlements(familyId);
  const inheritedAccess = isFree && hasPlus;
  const [loading, setLoading] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const queryClient = useQueryClient();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutTarget, setCheckoutTarget] = useState<"plus" | "together">("plus");
  const [showSammenSell, setShowSammenSell] = useState(false);
  const { user } = useAuth();

  const planLabels: Record<string, string> = {
    free: "Flyt Gratis",
    plus: "Flyt Pluss",
    together: "Flyt Sammen",
  };

  function openCheckout(target: "plus" | "together") {
    setCheckoutTarget(target);
    setCheckoutOpen(true);
  }

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base font-semibold">Abonnement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        {inheritedAccess ? (
          /* ── Inherited Plus via Sammen: informational card ── */
          <div className="space-y-2">
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-2">
              <div className="flex items-center gap-2">
                <HeartHandshake className="h-5 w-5 text-primary shrink-0" />
                <p className="font-semibold text-sm">Pluss-tilgang inkludert</p>
              </div>
              <p className="text-xs text-muted-foreground">
                Du har tilgang til alle Pluss-funksjoner gjennom medforelderens Flyt Sammen-abonnement. Full historikk, rapporter og innsikt er inkludert.
              </p>
            </div>
          </div>
        ) : isFree ? (
          /* ── Free plan: rich upgrade cards ── */
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs font-normal">Gratis</Badge>
              <span className="text-sm text-muted-foreground">Ubegrenset barn · 7 dager historikk</span>
            </div>

            {/* Plus card */}
            <div
              className="relative rounded-xl border-2 border-primary/40 bg-primary/5 p-4 space-y-2 cursor-pointer hover:border-primary/70 transition-colors"
              onClick={() => openCheckout("plus")}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">Flyt Pluss</p>
                  <p className="text-xs text-muted-foreground">79 kr/mnd</p>
                </div>
                <Badge className="bg-primary text-primary-foreground text-xs">14 dager gratis</Badge>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 pl-0">
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Full historikk og utvikling over tid</li>
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Full historikk og rapporter</li>
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Ukeplanimport fra skolen</li>
              </ul>
            </div>

            {/* Together card */}
            <div
              className="rounded-xl border border-border p-4 space-y-2 cursor-pointer hover:border-muted-foreground/40 transition-colors"
              onClick={() => openCheckout("together")}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm">Flyt Sammen</p>
                  <p className="text-xs text-muted-foreground">99 kr/mnd</p>
                </div>
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-xs text-muted-foreground">
                Alt i Flyt Pluss + felles foreldredashboard, tydelig ansvarsfordeling og en samlet struktur barnet kan stole på.
              </p>
            </div>
          </div>
        ) : (
          /* ── Paid plan: compact status + manage button ── */
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{planLabels[plan] ?? plan}</p>
                {isTrialing && trialEndsAt && (
                  <p className="text-xs text-muted-foreground">
                    Prøveperiode til {new Date(trialEndsAt).toLocaleDateString("nb-NO")}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setManageOpen(true)}>
                Administrer
              </Button>
            </div>

            {plan === "plus" && (
              <>
                <Button variant="outline" size="sm" className="w-full border-primary/30 text-primary hover:bg-primary/5" onClick={() => setShowSammenSell(true)}>
                  Oppgrader til Flyt Sammen
                </Button>

                {/* Selling dialog for Sammen */}
                <Dialog open={showSammenSell} onOpenChange={setShowSammenSell}>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle className="text-center text-lg">Flyt Sammen</DialogTitle>
                      <DialogDescription className="text-center">
                        Samarbeid og konsistens – for foreldre som vil stå samlet.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                          <Users className="h-5 w-5 text-primary mx-auto" />
                          <p className="text-xs font-medium">Felles dashboard</p>
                          <p className="text-[11px] text-muted-foreground">Se alt samlet, begge foreldre</p>
                        </div>
                        <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                          <Eye className="h-5 w-5 text-primary mx-auto" />
                          <p className="text-xs font-medium">Tydelig ansvarsfordeling</p>
                          <p className="text-[11px] text-muted-foreground">Hvem følger opp hva</p>
                        </div>
                        <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                          <Shield className="h-5 w-5 text-primary mx-auto" />
                          <p className="text-xs font-medium">Samlet struktur</p>
                          <p className="text-[11px] text-muted-foreground">Barnet møter like forventninger</p>
                        </div>
                        <div className="rounded-lg border border-border p-3 space-y-1.5 text-center">
                          <HeartHandshake className="h-5 w-5 text-primary mx-auto" />
                          <p className="text-xs font-medium">Mindre friksjon</p>
                          <p className="text-[11px] text-muted-foreground">Færre diskusjoner, mer samspill</p>
                        </div>
                      </div>

                      <div className="rounded-lg bg-primary/5 border border-primary/20 p-3 space-y-1.5">
                        <p className="text-xs font-semibold text-center">Når dere står samlet</p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Alt fra Flyt Pluss inkludert</li>
                          <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Inviter medforelder til familien</li>
                          <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-primary shrink-0" /> Barnet slipper å forhandle</li>
                        </ul>
                      </div>

                      <div className="text-center space-y-2">
                        <Button
                          className="w-full"
                          disabled={loading}
                          onClick={async () => {
                            setLoading(true);
                            try {
                              const { error } = await supabase.functions.invoke("manage-subscription", {
                                body: { action: "change-plan", plan: "together" },
                              });
                              if (error) throw error;
                              // Optimistic local DB update
                              if (user) {
                                await supabase.from("subscriptions").upsert(
                                  [{ user_id: user.id, plan: "together" as const }],
                                  { onConflict: "user_id" }
                                );
                              }
                              toast.success("Oppgradert til Flyt Sammen! 🎉");
                              queryClient.invalidateQueries({ queryKey: ["subscription"] });
                              queryClient.invalidateQueries({ queryKey: ["subscription-sync"] });
                              queryClient.invalidateQueries({ queryKey: ["family-entitlement"] });
                              setShowSammenSell(false);
                            } catch {
                              toast.error("Kunne ikke oppgradere til Flyt Sammen");
                            } finally {
                              setLoading(false);
                            }
                          }}
                        >
                          {loading ? "Oppgraderer…" : "Oppgrader nå – 99 kr/mnd"}
                        </Button>
                        <p className="text-[11px] text-muted-foreground">
                          Prisforskjellen beregnes forholdsmessig. Ingen ekstra binding.
                        </p>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </>
        )}
      </CardContent>

      <ManageSubscriptionDialog open={manageOpen} onOpenChange={setManageOpen} />
      <EmbeddedCheckoutDialog
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        priceId={PLAN_CONFIG[checkoutTarget].price_id}
        planLabel={planLabels[checkoutTarget]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["subscription"] });
          queryClient.invalidateQueries({ queryKey: ["family-entitlement"] });
        }}
      />
    </Card>
  );
}

// ═══════════════════════════════════════════════════════
// Section: Co-parent overview
// ═══════════════════════════════════════════════════════
function CoparentSection({
  familyId,
  members,
  userId,
  childCoparents,
  children,
  hasTogether,
  hasPlus,
}: {
  familyId: string | null;
  members: FamilyMember[];
  userId: string | undefined;
  childCoparents: { child_id: string }[];
  children: Child[];
  hasTogether: boolean;
  hasPlus: boolean;
}) {
  // Query all family invites (to show status)
  const { data: allFamilyInvites = [] } = useQuery({
    queryKey: ["family-invites-all", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_invites")
        .select("id, invite_code, created_at, expires_at, used_by_user_id, child_ids, clicked_at")
        .eq("family_id", familyId!)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: !!familyId,
  });

  const now = new Date().toISOString();
  const pendingFamilyInvites = allFamilyInvites.filter(
    (i) => !i.used_by_user_id && i.expires_at > now
  );
  const acceptedInvites = allFamilyInvites.filter((i) => !!i.used_by_user_id);
  const expiredInvites = allFamilyInvites.filter(
    (i) => !i.used_by_user_id && i.expires_at <= now
  );

  // Enrich coparents with child info
  const coparentsByChild = children
    .filter((c) => childCoparents.some((cp) => cp.child_id === c.id))
    .map((c) => ({
      child: c,
      hasCoparent: true,
    }));

  const hasAnyStatus = coparentsByChild.length > 0 || pendingFamilyInvites.length > 0;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base font-semibold">Medforelder</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        {coparentsByChild.length > 0 && (
          <div className="space-y-1.5">
            {coparentsByChild.map(({ child }) => (
              <div
                key={child.id}
                className="flex items-center gap-3 rounded-lg border p-3"
              >
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium flex-1">{child.name}</span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Check className="h-3 w-3" /> Koblet
                </span>
              </div>
            ))}
          </div>
        )}

        {pendingFamilyInvites.length > 0 && (
          <div className="space-y-1.5">
            {pendingFamilyInvites.map((invite) => {
              const childNames = (invite.child_ids as string[] | null)
                ?.map((id: string) => children.find((c) => c.id === id)?.name)
                .filter(Boolean)
                .join(", ");
              const hasClicked = !!(invite as any).clicked_at;
              return (
                <div
                  key={invite.id}
                  className={`flex items-center gap-3 rounded-lg border border-dashed p-3 ${
                    hasClicked
                      ? "border-blue-300 bg-blue-50/50"
                      : "border-amber-300 bg-amber-50/50"
                  }`}
                >
                  {hasClicked ? (
                    <Eye className="h-4 w-4 text-blue-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-amber-500" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {hasClicked ? "Lenken er åpnet" : "Venter på svar"}
                    </p>
                    {hasClicked && (
                      <p className="text-xs text-blue-600">Venter på registrering</p>
                    )}
                    {childNames && (
                      <p className="text-xs text-muted-foreground truncate">For: {childNames}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Utløper {new Date(invite.expires_at).toLocaleDateString("nb-NO")}
                    </p>
                  </div>
                  <Badge variant="outline" className={`text-xs shrink-0 ${
                    hasClicked
                      ? "border-blue-300 text-blue-600"
                      : "border-amber-300 text-amber-600"
                  }`}>
                    {hasClicked ? "Åpnet" : "Venter"}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}

        {acceptedInvites.length > 0 && (
          <div className="space-y-1.5">
            {acceptedInvites.map((invite) => {
              const childNames = (invite.child_ids as string[] | null)
                ?.map((id: string) => children.find((c) => c.id === id)?.name)
                .filter(Boolean)
                .join(", ");
              return (
                <div
                  key={invite.id}
                  className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50/50 p-3"
                >
                  <Check className="h-4 w-4 text-green-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Invitasjon akseptert</p>
                    {childNames && (
                      <p className="text-xs text-muted-foreground truncate">For: {childNames}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {new Date(invite.created_at).toLocaleDateString("nb-NO")}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs border-green-300 text-green-600 shrink-0">Akseptert</Badge>
                </div>
              );
            })}
          </div>
        )}

        {expiredInvites.length > 0 && (
          <div className="space-y-1.5">
            {expiredInvites.map((invite) => (
              <div
                key={invite.id}
                className="flex items-center gap-3 rounded-lg border border-dashed p-3 opacity-60"
              >
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Invitasjon utløpt</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(invite.expires_at).toLocaleDateString("nb-NO")}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs shrink-0">Utløpt</Badge>
              </div>
            ))}
          </div>
        )}

        {!hasAnyStatus && (
          <p className="text-sm text-muted-foreground">
            Ingen medforelder lagt til ennå.
          </p>
        )}

        {hasTogether ? (
          <Link to="/settings/invite-coparent">
            <Button variant="outline" size="sm" className="w-full gap-2">
              <Users className="h-4 w-4" />
              Legg til medforelder
            </Button>
          </Link>
        ) : (
          <div className="space-y-3">
            <UpgradeHint
              title="Samarbeid med medforelder"
              description="Med Flyt Sammen kan dere stå samlet – felles dashboard, ansvarsfordeling og én struktur barnet kan stole på."
              targetPlan="together"
              ctaLabel="Oppgrader til Flyt Sammen"
              useCheckout
              familyId={familyId ?? undefined}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
// ═══════════════════════════════════════════════════════
// Section: Feature Flags Toggle
// ═══════════════════════════════════════════════════════
const FLAG_LABELS: Record<FeatureFlag, string> = {
  beta_features: "Beta-funksjoner",
  debug_banner: "Debug-banner",
  spond_integration: "Spond-integrasjon",
  ai_coach_child: "AI-forslag for barn",
  ai_coach_parent: "AI-forslag for foreldre",
};

/** Flags that appear in the beta features sub-list, not in the main toggles */
const BETA_SUB_FLAGS: FeatureFlag[] = ["spond_integration"];

// Spond types (match Edge contracts)
type SpondLoginResponse = {
  myProfileId: string;
  accessibleChildren: Array<{
    profileId: string;
    name: string;
    memberships: Array<{ groupId: string; groupName: string; memberId: string; respondent: boolean }>;
  }>;
  unmappableChildren: Array<{ name: string; groupId: string; groupName?: string; memberId: string; respondent: boolean }>;
};
type SpondEventChild = { childId: string; spondProfileId?: string; memberId: string; rsvp: string };
type SpondEventItem = {
  spondEventId: string;
  _groupId: string;
  _groupName?: string;
  title: string;
  startTimestamp: string;
  endTimestamp: string;
  specificDate?: string;
  startTime?: string;
  endTime?: string;
  children: SpondEventChild[];
  meta?: { detailFetchFailed?: boolean };
};

function FeatureFlagsToggle({ familyId, children: appChildren }: { familyId: string | null; children: Child[] }) {
  const { user } = useAuth();
  const { hasFlag, isLoading, flags } = useFeatureFlags();
  const queryClient = useQueryClient();
  const [spondOpen, setSpondOpen] = useState(false);
  const [spondEmail, setSpondEmail] = useState("");
  const [spondPassword, setSpondPassword] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [spondStep, setSpondStep] = useState<"login" | "map" | "groups" | "import">("login");
  const [loginData, setLoginData] = useState<SpondLoginResponse | null>(null);
  const [spondError, setSpondError] = useState<string | null>(null);
  const [childMapping, setChildMapping] = useState<Record<string, string>>({});
  const [unmappableMapping, setUnmappableMapping] = useState<Record<string, string>>({}); // key: `${groupId}:${memberId}` -> Flyt child_id
  const [groupSelections, setGroupSelections] = useState<Record<string, string[]>>({});
  const [savingMapping, setSavingMapping] = useState(false);
  const [savingGroups, setSavingGroups] = useState(false);
  const [importing, setImporting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isReconfiguring, setIsReconfiguring] = useState(false);
  const [importTestResult, setImportTestResult] = useState<{ eventCount: number } | { error: string } | null>(null);
  const [testFetching, setTestFetching] = useState(false);

  const getSpondErrorMessage = (
    invokeError: Error | null,
    dataError: string | null | undefined,
    context: "login" | "import"
  ): string => {
    const bodyMessage = dataError?.trim();
    const loginMessages: Record<string, string> = {
      Unauthorized: "Du må være innlogget i Flyt.",
      "Invalid token": "Du må være innlogget i Flyt.",
      "email and password are required": "Fyll inn e-post og passord.",
      "Spond login failed": "Feil e-post eller passord, eller så kan ikke Spond nås akkurat nå. Prøv igjen.",
      "Spond did not return tokens": "Spond svarte uventet. Prøv igjen om litt.",
      "Failed to fetch Spond profile": "Kunne ikke hente Spond-profil. Prøv igjen.",
      "Failed to store credentials": "Kunne ikke lagre tilkobling. Prøv igjen.",
      "Failed to fetch groups": "Innloggingen fungerte, men gruppelisten kunne ikke hentes. Du kan prøve å gå videre eller koble til på nytt senere.",
      "Internal server error": "Noe gikk galt på serveren. Prøv igjen senere.",
    };
    const importMessages: Record<string, string> = {
      Unauthorized: "Du må være innlogget i Flyt.",
      "Invalid token": "Du må være innlogget i Flyt.",
      token_expired: "Spond-tilkoblingen er utløpt. Logg inn på nytt.",
      "Internal server error": "Kunne ikke hente arrangementer. Prøv igjen senere.",
    };
    const map = context === "login" ? loginMessages : importMessages;
    if (bodyMessage && map[bodyMessage]) return map[bodyMessage];
    if (bodyMessage) return bodyMessage;
    const msg = invokeError?.message?.trim();
    if (msg && (msg.includes("non-2xx") || msg.includes("Edge Function"))) {
      return context === "login"
        ? "Kunne ikke koble til Spond. Sjekk e-post og passord, eller prøv igjen senere."
        : "Kunne ikke hente arrangementer fra Spond. Prøv igjen eller logg inn på nytt.";
    }
    if (msg) return msg;
    return context === "login" ? "Kunne ikke koble til Spond. Prøv igjen." : "Kunne ikke hente arrangementer. Prøv igjen.";
  };

  const { data: spondProfiles = [], isFetched: spondProfilesFetched } = useQuery({
    queryKey: ["child-spond-profile", familyId],
    queryFn: async () => {
      if (!familyId) return [];
      const { data, error } = await supabase.from("child_spond_profile").select("child_id, spond_profile_id").eq("family_id", familyId);
      if (error) throw error;
      return (data ?? []) as { child_id: string; spond_profile_id: string }[];
    },
    enabled: !!familyId && spondOpen,
  });

  const { data: spondGroups = [], isFetched: spondGroupsFetched } = useQuery({
    queryKey: ["child-spond-groups", familyId],
    queryFn: async () => {
      if (!familyId) return [];
      const { data, error } = await supabase.from("child_spond_groups").select("child_id, spond_group_id, spond_group_name, spond_member_id").eq("family_id", familyId);
      if (error) throw error;
      return (data ?? []) as unknown as { child_id: string; spond_group_id: string; spond_group_name: string | null; spond_member_id: string | null }[];
    },
    enabled: !!familyId && spondOpen,
  });

  const spondInitialLoading = spondOpen && spondStep === "login" && (!spondProfilesFetched || !spondGroupsFetched);

  const { data: spondStatus } = useQuery({
    queryKey: ["spond-integration-status", familyId],
    queryFn: async () => {
      if (!familyId) return { eventCount: 0, lastSyncedAt: null as string | null };
      const [countRes, latestRes] = await Promise.all([
        supabase.from("child_calendar_events").select("id", { count: "exact", head: true }).eq("family_id", familyId).eq("source", "spond_import"),
        supabase.from("child_calendar_events").select("created_at").eq("family_id", familyId).eq("source", "spond_import").order("created_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      const eventCount = countRes.count ?? 0;
      const lastSyncedAt = latestRes.data?.created_at ?? null;
      return { eventCount, lastSyncedAt };
    },
    enabled: !!familyId && spondOpen && (spondProfiles.length > 0 || spondGroups.length > 0),
  });

  const handleSpondOpenChange = (open: boolean) => {
    setSpondOpen(open);
    if (!open) {
      setSpondStep("login");
      setLoginData(null);
      setIsReconfiguring(false);
      setImportTestResult(null);
      setSpondError(null);
      setSpondEmail("");
      setSpondPassword("");
      setUnmappableMapping({});
    }
  };

  const handleSpondConnect = async () => {
    if (!spondEmail.trim() || !spondPassword.trim()) return;
    setConnecting(true);
    setSpondError(null);
    try {
      // JWT is sent automatically by supabase.functions.invoke() when user is logged in.
      const { data, error } = await supabase.functions.invoke("spond-login", {
        body: { email: spondEmail.trim(), password: spondPassword },
      });
      const dataErr = (data as { error?: string })?.error;
      if (error || dataErr) {
        const userMsg = getSpondErrorMessage(error ?? null, dataErr ?? null, "login");
        throw new Error(userMsg);
      }
      setLoginData(data as SpondLoginResponse);
      setIsReconfiguring(false);
      setSpondStep("map");
      const initialMapping: Record<string, string> = {};
      for (const p of spondProfiles) {
        initialMapping[p.spond_profile_id] = p.child_id;
      }
      setChildMapping(initialMapping);
      const initialUnmappable: Record<string, string> = {};
      for (const g of spondGroups) {
        if (g.spond_member_id) {
          initialUnmappable[`${g.spond_group_id}:${g.spond_member_id}`] = g.child_id;
        }
      }
      setUnmappableMapping(initialUnmappable);
      const initialGroups: Record<string, string[]> = {};
      for (const c of appChildren) {
        initialGroups[c.id] = spondGroups.filter((g) => g.child_id === c.id).map((g) => g.spond_group_id);
      }
      setGroupSelections(initialGroups);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Noe gikk galt";
      toast.error(msg);
      setSpondError(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!familyId || !loginData) return;
    setSavingMapping(true);
    try {
      for (const ac of loginData.accessibleChildren) {
        const childId = childMapping[ac.profileId];
        if (childId) {
          await supabase.from("child_spond_profile").upsert(
            { child_id: childId, family_id: familyId, spond_profile_id: ac.profileId, spond_display_name: ac.name },
            { onConflict: "child_id" }
          );
        }
      }
      for (const u of loginData.unmappableChildren) {
        const key = `${u.groupId}:${u.memberId}`;
        const childId = unmappableMapping[key];
        if (childId) {
          await supabase.from("child_spond_groups").upsert(
            {
              child_id: childId,
              family_id: familyId,
              spond_group_id: u.groupId,
              spond_group_name: u.groupName ?? null,
              spond_member_id: u.memberId,
            },
            { onConflict: "child_id, spond_group_id" }
          );
        }
      }
      queryClient.invalidateQueries({ queryKey: ["child-spond-profile", familyId] });
      queryClient.invalidateQueries({ queryKey: ["child-spond-groups", familyId] });
      setGroupSelections((prev) => {
        const next = { ...prev };
        for (const u of loginData.unmappableChildren) {
          const key = `${u.groupId}:${u.memberId}`;
          const childId = unmappableMapping[key];
          if (childId) {
            const arr = next[childId] ?? [];
            if (!arr.includes(u.groupId)) next[childId] = [...arr, u.groupId];
          }
        }
        return next;
      });
      setSpondStep("groups");
    } catch {
      toast.error("Kunne ikke lagre koblinger");
    } finally {
      setSavingMapping(false);
    }
  };

  const handleSaveGroups = async () => {
    if (!familyId) return;
    setSavingGroups(true);
    try {
      const childrenInScope = appChildren.filter(
        (c) =>
          (childMapping && Object.values(childMapping).includes(c.id)) ||
          spondGroups.some((g) => g.child_id === c.id && g.spond_member_id)
      );
      for (const child of childrenInScope) {
        const selected = groupSelections[child.id] ?? [];
        await supabase.from("child_spond_groups").delete().eq("child_id", child.id).eq("family_id", familyId);
        for (const gid of selected) {
          const existing = spondGroups.find((g) => g.child_id === child.id && g.spond_group_id === gid);
          await supabase.from("child_spond_groups").insert({
            child_id: child.id,
            family_id: familyId,
            spond_group_id: gid,
            spond_group_name: existing?.spond_group_name ?? null,
            spond_member_id: existing?.spond_member_id ?? null,
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: ["child-spond-groups", familyId] });
      setImportTestResult(null);
      setSpondStep("import");
    } catch {
      toast.error("Kunne ikke lagre grupper");
    } finally {
      setSavingGroups(false);
    }
  };

  const buildImportPayload = (): Array<{ childId: string; spondProfileId?: string; groups: Array<{ groupId: string; memberId?: string }> }> | null => {
    if (!familyId) return null;
    const payloadMap = new Map<string, { spondProfileId: string | null; groups: Array<{ groupId: string; memberId?: string }> }>();
    for (const c of appChildren) {
      const profileId = Object.entries(childMapping).find(([, id]) => id === c.id)?.[0];
      const selectedIds = groupSelections[c.id] ?? [];
      if (profileId && selectedIds.length > 0) {
        const existing = payloadMap.get(c.id) ?? { spondProfileId: null as string | null, groups: [] };
        existing.spondProfileId = profileId;
        for (const gid of selectedIds) {
          if (!existing.groups.some((g) => g.groupId === gid)) existing.groups.push({ groupId: gid });
        }
        payloadMap.set(c.id, existing);
      }
    }
    for (const g of spondGroups) {
      if (!g.spond_member_id) continue;
      const selected = groupSelections[g.child_id] ?? [];
      if (!selected.includes(g.spond_group_id)) continue;
      const existing = payloadMap.get(g.child_id) ?? { spondProfileId: null, groups: [] };
      existing.groups.push({ groupId: g.spond_group_id, memberId: g.spond_member_id });
      payloadMap.set(g.child_id, existing);
    }
    const payload = Array.from(payloadMap.entries())
      .filter(([, p]) => p.groups.length > 0)
      .map(([childId, p]) => ({ childId, spondProfileId: p.spondProfileId ?? undefined, groups: p.groups }));
    return payload.length > 0 ? payload : null;
  };

  const handleTestImport = async () => {
    const childrenPayload = buildImportPayload();
    if (!childrenPayload) {
      toast.error("Velg minst ett barn med minst én gruppe");
      return;
    }
    setTestFetching(true);
    setSpondError(null);
    setImportTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("spond-events", {
        body: { children: childrenPayload, includeDetails: true } as { children: typeof childrenPayload; includeDetails: boolean },
      });
      const dataErr = (data as { error?: string })?.error;
      if (error || dataErr) {
        const userMsg = getSpondErrorMessage(error ?? null, dataErr ?? null, "import");
        if (dataErr === "token_expired") {
          toast.error(userMsg);
          setSpondStep("login");
          setLoginData(null);
          return;
        }
        setImportTestResult({ error: userMsg });
        return;
      }
      const events = (data as { events?: SpondEventItem[] }).events ?? [];
      const dedupe = new Set<string>();
      for (const ev of events) {
        for (const ch of ev.children) dedupe.add(`${ev.spondEventId}:${ch.childId}`);
      }
      setImportTestResult({ eventCount: dedupe.size });
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const technical = /duplicate|constraint|violates|ECONNREFUSED|timeout|non-2xx|Edge Function/i.test(raw);
      setImportTestResult({ error: technical ? "Kunne ikke hente arrangementer. Prøv igjen." : raw });
    } finally {
      setTestFetching(false);
    }
  };

  const handleImport = async () => {
    if (!familyId || !user) return;
    const childrenPayload = buildImportPayload();
    if (!childrenPayload) {
      toast.error("Velg minst ett barn med minst én gruppe");
      return;
    }
    setImporting(true);
    setSpondError(null);
    try {
      // JWT is sent automatically by supabase.functions.invoke() when user is logged in.
      const { data, error } = await supabase.functions.invoke("spond-events", {
        body: { children: childrenPayload, includeDetails: true } as { children: typeof childrenPayload; includeDetails: boolean },
      });
      const dataErr = (data as { error?: string })?.error;
      if (error || dataErr) {
        const userMsg = getSpondErrorMessage(error ?? null, dataErr ?? null, "import");
        if (dataErr === "token_expired") {
          toast.error(userMsg);
          setSpondStep("login");
          setLoginData(null);
          return;
        }
        throw new Error(userMsg);
      }
      const events = (data as { events?: SpondEventItem[] }).events ?? [];
      const importBatchId = crypto.randomUUID();
      const dedupe = new Set<string>();
      const rows: Array<Record<string, unknown>> = [];
      for (const ev of events) {
        for (const ch of ev.children) {
          const key = `${ev.spondEventId}:${ch.childId}`;
          if (dedupe.has(key)) continue;
          dedupe.add(key);
          const child = appChildren.find((c) => c.id === ch.childId);
          if (!child) continue;
          rows.push({
            child_id: ch.childId,
            family_id: familyId,
            event_type: "activity",
            title: ev.title,
            description: null,
            day_of_week: null,
            specific_date: ev.specificDate ?? null,
            start_time: ev.startTime ?? null,
            end_time: ev.endTime ?? null,
            recurring: false,
            source: "spond_import",
            source_ref: null,
            external_event_id: ev.spondEventId,
            metadata: {
              spondEventId: ev.spondEventId,
              spondGroupId: ev._groupId,
              spondProfileId: ch.spondProfileId,
              spondMemberId: ch.memberId,
              spondRsvp: ch.rsvp,
              spondStartUtc: ev.startTimestamp,
              spondEndUtc: ev.endTimestamp,
            },
            created_by: user.id,
            import_batch_id: importBatchId,
          });
        }
      }
      if (rows.length > 0) {
        const { error: upsertErr } = await supabase
          .from("child_calendar_events")
          .upsert(rows as any, { onConflict: "child_id,source,external_event_id" });
        if (upsertErr) throw upsertErr;
        queryClient.invalidateQueries({ queryKey: ["child-calendar-events"] });
        toast.success(`${rows.length} arrangementer importert fra Spond`);
      } else {
        toast.info("Ingen nye arrangementer å importere");
      }
      handleSpondOpenChange(false);
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const technical = /duplicate|constraint|violates|ECONNREFUSED|timeout|non-2xx|Edge Function/i.test(raw);
      const msg = technical ? "Kunne ikke importere arrangementer til kalenderen. Prøv igjen." : raw;
      toast.error(msg);
      setSpondError(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleSync = async () => {
    if (!familyId || !user) return;
    const payloadMap = new Map<string, { spondProfileId: string | null; groups: Array<{ groupId: string; memberId?: string }> }>();
    for (const g of spondGroups) {
      const existing = payloadMap.get(g.child_id) ?? { spondProfileId: null as string | null, groups: [] };
      const profileRow = spondProfiles.find((p) => p.child_id === g.child_id);
      if (profileRow) existing.spondProfileId = profileRow.spond_profile_id;
      existing.groups.push({ groupId: g.spond_group_id, memberId: g.spond_member_id ?? undefined });
      payloadMap.set(g.child_id, existing);
    }
    const childrenPayload = Array.from(payloadMap.entries())
      .filter(([, p]) => p.groups.length > 0)
      .map(([childId, p]) => ({ childId, spondProfileId: p.spondProfileId ?? undefined, groups: p.groups }));
    if (childrenPayload.length === 0) {
      toast.error("Ingen barn med grupper er koblet til Spond");
      return;
    }
    setSyncing(true);
    setSpondError(null);
    try {
      const { data, error } = await supabase.functions.invoke("spond-events", {
        body: { children: childrenPayload, includeDetails: true } as { children: typeof childrenPayload; includeDetails: boolean },
      });
      const dataErr = (data as { error?: string })?.error;
      if (error || dataErr) {
        const userMsg = getSpondErrorMessage(error ?? null, dataErr ?? null, "import");
        if (dataErr === "token_expired") {
          toast.error(userMsg);
          setIsReconfiguring(true);
          setSpondStep("login");
          setLoginData(null);
          return;
        }
        throw new Error(userMsg);
      }
      const events = (data as { events?: SpondEventItem[] }).events ?? [];
      const importBatchId = crypto.randomUUID();
      const dedupe = new Set<string>();
      const rows: Array<Record<string, unknown>> = [];
      for (const ev of events) {
        for (const ch of ev.children) {
          const key = `${ev.spondEventId}:${ch.childId}`;
          if (dedupe.has(key)) continue;
          dedupe.add(key);
          const child = appChildren.find((c) => c.id === ch.childId);
          if (!child) continue;
          rows.push({
            child_id: ch.childId,
            family_id: familyId,
            event_type: "activity",
            title: ev.title,
            description: null,
            day_of_week: null,
            specific_date: ev.specificDate ?? null,
            start_time: ev.startTime ?? null,
            end_time: ev.endTime ?? null,
            recurring: false,
            source: "spond_import",
            source_ref: null,
            external_event_id: ev.spondEventId,
            metadata: {
              spondEventId: ev.spondEventId,
              spondGroupId: ev._groupId,
              spondProfileId: ch.spondProfileId,
              spondMemberId: ch.memberId,
              spondRsvp: ch.rsvp,
              spondStartUtc: ev.startTimestamp,
              spondEndUtc: ev.endTimestamp,
            },
            created_by: user.id,
            import_batch_id: importBatchId,
          });
        }
      }
      if (rows.length > 0) {
        const { error: upsertErr } = await supabase
          .from("child_calendar_events")
          .upsert(rows as any, { onConflict: "child_id,source,external_event_id" });
        if (upsertErr) throw upsertErr;
        queryClient.invalidateQueries({ queryKey: ["child-calendar-events"] });
        queryClient.invalidateQueries({ queryKey: ["spond-integration-status", familyId] });
        toast.success(`${rows.length} arrangementer synkronisert fra Spond`);
      } else {
        toast.info("Ingen nye arrangementer å synkronisere");
      }
    } catch (e) {
      const raw = e instanceof Error ? e.message : String(e);
      const technical = /duplicate|constraint|violates|ECONNREFUSED|timeout|non-2xx|Edge Function/i.test(raw);
      const msg = technical ? "Kunne ikke synkronisere. Prøv igjen." : raw;
      toast.error(msg);
      setSpondError(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnectSpond = async () => {
    if (!user || !familyId) return;
    setDisconnecting(true);
    try {
      await supabase.from("spond_credentials").delete().eq("user_id", user.id);
      await supabase.from("child_spond_profile").delete().eq("family_id", familyId);
      await supabase.from("child_spond_groups").delete().eq("family_id", familyId);
      queryClient.invalidateQueries({ queryKey: ["child-spond-profile", familyId] });
      queryClient.invalidateQueries({ queryKey: ["child-spond-groups", familyId] });
      toast.success("Spond er koblet fra");
      handleSpondOpenChange(false);
    } catch {
      toast.error("Kunne ikke koble fra Spond. Prøv igjen.");
    } finally {
      setDisconnecting(false);
    }
  };

  // Non-beta admin flags (e.g. debug_banner)
  const otherAdminFlags = (flags
    .map((f) => f.flag) as FeatureFlag[])
    .filter((f) => ADMIN_ONLY_FLAGS.includes(f) && !BETA_SUB_FLAGS.includes(f) && f !== "beta_features");
  // Exclude AI coach flags – they have their own dedicated section (AiCoachingSection)
  const AI_SECTION_FLAGS: FeatureFlag[] = ["ai_coach_child", "ai_coach_parent"];
  const visibleOtherFlags = [...new Set([...SELF_SERVICE_FLAGS, ...otherAdminFlags])].filter(
    (f) => !AI_SECTION_FLAGS.includes(f)
  );

  const hasBetaFlag = flags.some((f) => f.flag === "beta_features");
  const betaEnabled = hasFlag("beta_features");

  const toggle = async (flag: FeatureFlag) => {
    if (!user) return;
    const currentlyEnabled = hasFlag(flag);

    if (currentlyEnabled) {
      await supabase
        .from("feature_flags")
        .update({ enabled: false })
        .eq("user_id", user.id)
        .eq("flag", flag);
    } else {
      await supabase
        .from("feature_flags")
        .update({ enabled: true })
        .eq("user_id", user.id)
        .eq("flag", flag);
    }
    queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
  };

  if (isLoading) return null;

  const showOtherFlags = visibleOtherFlags.length > 0;
  if (!hasBetaFlag && !showOtherFlags) return null;

  return (
    <>
      {showOtherFlags && (
        <div className="space-y-2">
          {visibleOtherFlags.map((flag) => (
            <div key={flag} className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm">{FLAG_LABELS[flag]}</span>
              <Switch
                checked={hasFlag(flag)}
                onCheckedChange={() => toggle(flag)}
              />
            </div>
          ))}
        </div>
      )}

      {hasBetaFlag && (
        <Card className="mt-3">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" />
                Beta-funksjoner
              </CardTitle>
              <Switch
                checked={betaEnabled}
                onCheckedChange={() => toggle("beta_features")}
              />
            </div>
          </CardHeader>
          {betaEnabled && (
            <CardContent className="space-y-2 p-4 pt-0">
              <p className="text-xs text-muted-foreground">
                Disse funksjonene er under utvikling og kan endre seg.
              </p>

              <div
                role="button"
                tabIndex={0}
                aria-label="Spond – Importer aktiviteter fra Spond"
                className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setSpondOpen(true)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSpondOpen(true); } }}
              >
                <img src="/spond_logo.svg" alt="" className="h-6 w-auto shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Importer aktiviteter fra Spond</p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </div>

              <Dialog open={spondOpen} onOpenChange={handleSpondOpenChange}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader className="space-y-2">
                    <DialogTitle className="sr-only">Spond</DialogTitle>
                    <div className="flex min-w-0 shrink-0" aria-hidden="true">
                      <img src="/spond_logo.svg" alt="" className="h-5 w-auto max-w-[120px] object-contain object-left" />
                    </div>
                    <DialogDescription className="text-left">
                      {spondInitialLoading && "Laster…"}
                      {!spondInitialLoading && spondStep === "login" && (spondProfiles.length > 0 || spondGroups.length > 0) && !isReconfiguring
                        ? "Få aktiviteter og arrangementer fra Spond direkte i kalenderen for barna."
                        : !spondInitialLoading && spondStep === "login" && "Logg inn med din Spond-konto."}
                      {spondStep === "map" && "Knytt Spond-barn til barn i Flyt (gjelder alle grupper)."}
                      {spondStep === "groups" && "Velg hvilke grupper som skal importeres til kalenderen for hvert barn."}
                      {spondStep === "import" && "Test at henting fungerer, deretter aktiver integrasjonen for å legge arrangementer i kalenderen."}
                    </DialogDescription>
                  </DialogHeader>

                  {spondInitialLoading ? (
                    <div className="space-y-4 py-2" aria-busy="true" aria-live="polite">
                      <Skeleton className="h-20 w-full rounded-lg" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                      <Skeleton className="h-10 w-full rounded-lg" />
                    </div>
                  ) : null}

                  {!spondInitialLoading && spondError && (
                    <div className="flex items-center gap-2 rounded-md bg-destructive/10 text-destructive text-sm p-2">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {spondError}
                    </div>
                  )}

                  {!spondInitialLoading && spondStep === "login" && (spondProfiles.length > 0 || spondGroups.length > 0) && !isReconfiguring ? (
                    <div className="space-y-4 py-2">
                      <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm">
                        <p className="font-medium flex items-center gap-2">
                          Status
                          <span className="relative flex h-2 w-2 shrink-0">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                          </span>
                        </p>
                        <div className="text-muted-foreground space-y-0.5 text-sm">
                          <p>{spondProfiles.length} barn koblet til, {spondGroups.length} {spondGroups.length === 1 ? "gruppe" : "grupper"}.</p>
                          {spondStatus != null && (
                            <>
                              <p>{spondStatus.eventCount} arrangementer i kalenderen.</p>
                              {spondStatus.lastSyncedAt && (
                                <p>Sist synkronisert: {new Date(spondStatus.lastSyncedAt).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}.</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button variant="default" className="w-full" onClick={handleSync} disabled={syncing}>
                          {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Synkroniser arrangementer
                        </Button>
                        <Button variant="outline" className="w-full" onClick={() => setIsReconfiguring(true)}>
                          Rekonfigurer kobling
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-muted-foreground hover:text-destructive"
                              disabled={disconnecting}
                            >
                              {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Koble fra Spond"}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Koble fra Spond?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Koblingen fjernes og importerte arrangementer ligger fortsatt i kalenderen. Du kan koble til på nytt når som helst.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDisconnectSpond()}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Koble fra
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ) : !spondInitialLoading && spondStep === "login" && (
                    <form autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSpondConnect(); }} className="space-y-3 py-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="spond-email">E-post</Label>
                        <Input
                          id="spond-email"
                          type="email"
                          placeholder="din@epost.no"
                          value={spondEmail}
                          onChange={(e) => setSpondEmail(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="spond-password">Passord</Label>
                        <Input
                          id="spond-password"
                          type="password"
                          placeholder="••••••••"
                          value={spondPassword}
                          onChange={(e) => setSpondPassword(e.target.value)}
                          autoComplete="off"
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={connecting || !spondEmail.trim() || !spondPassword.trim()}
                      >
                        {connecting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Kobler til…
                          </>
                        ) : (
                          "Koble til"
                        )}
                      </Button>
                    </form>
                  )}

                  {spondStep === "map" && loginData && (
                    <div className="space-y-4 py-2">
                      <div className="space-y-2">
                        {loginData.accessibleChildren.map((ac) => (
                          <div key={ac.profileId} className="flex items-center gap-2">
                            <span className="text-sm shrink-0 min-w-0 flex-1 truncate">{ac.name}</span>
                            <Select
                              value={childMapping[ac.profileId] ?? ""}
                              onValueChange={(v) => setChildMapping((prev) => ({ ...prev, [ac.profileId]: v }))}
                            >
                              <SelectTrigger className="w-28 shrink-0 ml-auto">
                                <SelectValue placeholder="Velg barn" />
                              </SelectTrigger>
                              <SelectContent>
                                {appChildren.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                      {loginData.unmappableChildren.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">Barn uten Spond-profil (kan synkroniseres per gruppe)</p>
                          <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/50 dark:bg-amber-950/30 dark:border-amber-800 p-2">
                            {loginData.unmappableChildren.map((u) => {
                              const key = `${u.groupId}:${u.memberId}`;
                              return (
                                <div key={key} className="flex items-center gap-2">
                                  <span className="text-sm shrink-0 min-w-0 flex-1 truncate">{u.name}</span>
                                  <span className="text-xs text-muted-foreground truncate shrink-0">{u.groupName || u.groupId}</span>
                                  <Select
                                    value={unmappableMapping[key] ?? ""}
                                    onValueChange={(v) => setUnmappableMapping((prev) => ({ ...prev, [key]: v }))}
                                  >
                                    <SelectTrigger className="w-28 shrink-0 ml-auto">
                                      <SelectValue placeholder="Velg barn" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {appChildren.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSpondStep("login")}
                          disabled={savingMapping}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Tilbake
                        </Button>
                        <Button className="flex-1" onClick={handleSaveMapping} disabled={savingMapping}>
                          {savingMapping ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Fortsett og velg grupper
                        </Button>
                      </div>
                    </div>
                  )}

                  {spondStep === "groups" && loginData && (
                    <div className="space-y-4 py-2">
                      {appChildren
                        .filter(
                          (c) =>
                            (childMapping && Object.values(childMapping).includes(c.id)) ||
                            spondGroups.some((g) => g.child_id === c.id && g.spond_member_id)
                        )
                        .map((child) => {
                          const profileId = Object.entries(childMapping).find(([, id]) => id === child.id)?.[0];
                          const memberships = loginData!.accessibleChildren.find((ac) => ac.profileId === profileId)?.memberships ?? [];
                          const selected = groupSelections[child.id] ?? [];
                          const memberIdOnlyGroups = spondGroups.filter((g) => g.child_id === child.id && g.spond_member_id);
                          return (
                            <div key={child.id} className="rounded-lg border p-3 space-y-2">
                              <p className="text-sm font-medium">{child.name}</p>
                              {memberships.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {memberships.map((m) => (
                                    <label key={m.groupId} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                      <Checkbox
                                        checked={selected.includes(m.groupId)}
                                        onCheckedChange={(checked) => {
                                          setGroupSelections((prev) => ({
                                            ...prev,
                                            [child.id]: checked
                                              ? [...(prev[child.id] ?? []), m.groupId]
                                              : (prev[child.id] ?? []).filter((id) => id !== m.groupId),
                                          }));
                                        }}
                                      />
                                      {m.groupName || m.groupId}
                                </label>
                                  ))}
                                </div>
                              )}
                              {memberIdOnlyGroups.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {memberIdOnlyGroups.map((g) => (
                                    <label key={g.spond_group_id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                                      <Checkbox
                                        checked={selected.includes(g.spond_group_id)}
                                        onCheckedChange={(checked) => {
                                          setGroupSelections((prev) => ({
                                            ...prev,
                                            [child.id]: checked
                                              ? [...(prev[child.id] ?? []), g.spond_group_id]
                                              : (prev[child.id] ?? []).filter((id) => id !== g.spond_group_id),
                                          }));
                                        }}
                                      />
                                      {g.spond_group_name || g.spond_group_id}
                                      <span className="text-xs text-muted-foreground">(per gruppe)</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSpondStep("map")}
                          disabled={savingGroups}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Tilbake
                        </Button>
                        <Button className="flex-1" onClick={handleSaveGroups} disabled={savingGroups}>
                          {savingGroups ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                          Fortsett og hent arrangementer
                        </Button>
                      </div>
                    </div>
                  )}

                  {spondStep === "import" && (
                    <div className="space-y-4 py-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={handleTestImport}
                        disabled={testFetching || importing}
                      >
                        {testFetching ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Tester henting…
                          </>
                        ) : (
                          "Test henting av arrangementer"
                        )}
                      </Button>
                      {importTestResult && (
                        <div className="rounded-md border p-3 text-sm">
                          {"eventCount" in importTestResult ? (
                            <p className="text-muted-foreground">
                              <span className="font-medium text-foreground">{importTestResult.eventCount} arrangementer</span> funnet. Du kan nå aktivere integrasjonen.
                            </p>
                          ) : (
                            <p className="text-destructive">{importTestResult.error}</p>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => { setImportTestResult(null); setSpondStep("groups"); }}
                          disabled={importing}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Tilbake
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={handleImport}
                          disabled={importing || !importTestResult || !("eventCount" in importTestResult)}
                        >
                          {importing ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Aktiverer…
                            </>
                          ) : (
                            "Aktiver"
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                </DialogContent>
              </Dialog>
            </CardContent>
          )}
        </Card>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════
// Section: AI Coaching
// ═══════════════════════════════════════════════════════
function AiCoachingSection({ familyId, hasPlus }: { familyId: string | null; hasPlus: boolean }) {
  const { user } = useAuth();
  const { hasFlag, toggleFlag, isLoading: flagsLoading } = useFeatureFlags();
  const queryClient = useQueryClient();

  const childCoachEnabled = !flagsLoading && hasFlag("ai_coach_child");
  const parentCoachEnabled = !flagsLoading && hasFlag("ai_coach_parent");

  // Default both flags to enabled if they don't exist yet
  const { data: existingFlags } = useQuery({
    queryKey: ["ai-coach-flags-init", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("feature_flags")
        .select("flag")
        .eq("user_id", user.id)
        .in("flag", ["ai_coach_child", "ai_coach_parent"]);
      return data ?? [];
    },
    enabled: !!user,
    staleTime: Infinity,
  });

  // Auto-create default enabled flags if they don't exist
  useEffect(() => {
    if (!user || !existingFlags) return;
    const missing = (["ai_coach_child", "ai_coach_parent"] as const).filter(
      (f) => !existingFlags.some((ef) => ef.flag === f)
    );
    if (missing.length > 0) {
      Promise.all(
        missing.map((flag) =>
          supabase.from("feature_flags").insert({ user_id: user.id, flag, enabled: true })
        )
      ).then(() => {
        queryClient.invalidateQueries({ queryKey: ["feature-flags", user.id] });
      });
    }
  }, [existingFlags, user]);

  // Quota: count coaching uses
  const { data: coachQuota } = useQuery({
    queryKey: ["ai-coach-quota", familyId],
    queryFn: async () => {
      if (!familyId) return { child: 0, parent: 0 };
      const [childRes, parentRes] = await Promise.all([
        supabase
          .from("ai_suggestion_logs")
          .select("id", { count: "exact", head: true })
          .eq("family_id", familyId)
          .eq("actor_type", "coach"),
        supabase
          .from("ai_suggestion_logs")
          .select("id", { count: "exact", head: true })
          .eq("family_id", familyId)
          .eq("actor_type", "parent_coach"),
      ]);
      return { child: childRes.count ?? 0, parent: parentRes.count ?? 0 };
    },
    enabled: !!familyId && !hasPlus,
  });

  async function handleToggle(flag: "ai_coach_child" | "ai_coach_parent", enabled: boolean) {
    try {
      await toggleFlag(flag, enabled);
      queryClient.invalidateQueries({ queryKey: ["feature-flags", user?.id] });
      toast.success(enabled ? "AI-forslag aktivert" : "AI-forslag deaktivert");
    } catch {
      toast.error("Kunne ikke endre innstilling");
    }
  }

  if (flagsLoading) return null;

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base font-semibold">AI-hjelp</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-0">
        <p className="text-xs text-muted-foreground">
          AI hjelper med å gjøre oppgaver mer konkrete. Klassifisering av kategorier skjer alltid automatisk.
        </p>

        {/* Child coaching toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">AI-forslag for barn</p>
            <p className="text-xs text-muted-foreground">Hjelper barnet skrive konkrete oppgaver</p>
          </div>
          <Switch
            checked={childCoachEnabled}
            onCheckedChange={(checked) => handleToggle("ai_coach_child", checked)}
          />
        </div>

        {/* Parent coaching toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">AI-forslag for foreldre</p>
            <p className="text-xs text-muted-foreground">Utviklingsrettede forslag basert på barnets aktivitet</p>
          </div>
          <Switch
            checked={parentCoachEnabled}
            onCheckedChange={(checked) => handleToggle("ai_coach_parent", checked)}
          />
        </div>

        {/* Quota display for free users */}
        {!hasPlus && coachQuota && (
          <div className="rounded-lg bg-muted/50 p-2.5 space-y-1">
            <p className="text-xs text-muted-foreground">
              Brukt {coachQuota.child + coachQuota.parent} av 100 gratis AI-forslag
              <span className="text-muted-foreground/60"> ({coachQuota.child} barn + {coachQuota.parent} forelder)</span>
            </p>
            {coachQuota.child + coachQuota.parent >= 100 && (
              <p className="text-xs text-primary">
                Oppgrader til Flyt Pluss for ubegrenset AI-hjelp
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChecklistTemplatesSection({
  familyId,
  children,
}: {
  familyId: string | null;
  children: Child[];
}) {
  const { data: templates = [] } = useQuery({
    queryKey: ["checklist-templates", familyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("checklist_templates")
        .select("*")
        .eq("family_id", familyId!);
      if (error) throw error;
      return data as { id: string; name: string; family_id: string; child_id: string | null }[];
    },
    enabled: !!familyId,
  });

  const familyTemplate = templates.find((t) => !t.child_id);
  const childTemplateIds = new Set(templates.filter((t) => t.child_id).map((t) => t.child_id));

  return (
    <Card>
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-base font-semibold">Byttesjekklister</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 p-4 pt-0">
        {/* Family default template */}
        <Link
          to="/settings/templates"
          className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
        >
          <span className="text-lg leading-none">
            <ClipboardList className="h-[1.2em] w-[1.2em] text-muted-foreground" />
          </span>
          <div className="flex-1">
            <span className="text-sm font-medium">Standardmal</span>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>

        {/* Per-child templates */}
        {children.map((child) => {
          const hasOwnTemplate = childTemplateIds.has(child.id);
          return (
            <Link
              key={child.id}
              to={`/settings/templates/${child.id}`}
              className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
            >
              <span className="text-lg">{child.avatar_emoji}</span>
              <div className="flex-1">
                <span className="text-sm font-medium">{child.name}</span>
                <p className="text-xs text-muted-foreground">
                  {hasOwnTemplate ? "Har egen mal" : "Bruker standardmal"}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}

